import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Checkbox, message, Popconfirm, Space, Card, Typography } from 'antd';
import { User, Permission, Employee, userService, CreateUserRequest, UpdateUserRequest } from '../services/userService';

const { Title } = Typography;
const { Option } = Select;

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [form] = Form.useForm();

    const fetchData = async () => {
        try {
            setLoading(true);
            const [usersData, permissionsData, employeesData] = await Promise.all([
                userService.getUsers(),
                userService.getPermissions(),
                userService.getUnassignedEmployees(),
            ]);
            setUsers(usersData);
            setPermissions(permissionsData);
            setEmployees(employeesData);
        } catch (error) {
            message.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const showModal = async (user: User | null) => {
        setEditingUser(user);
        if (user) {
            setLoading(true);
            try {
                const fullUser = await userService.getUserById(user.id);
                form.setFieldsValue({
                    ...fullUser,
                    permissionIds: fullUser.permissionIds || [],
                    password: 'DO_NOT_CHANGE_PASSWORD',
                });
            } catch (error) {
                message.error('Failed to fetch user details for editing.');
                setIsModalVisible(false);
                setLoading(false);
                return;
            } finally {
                setLoading(false);
            }
        } else {
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingUser(null);
        form.resetFields();
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingUser) {
                const updateData: UpdateUserRequest = {
                    macc: values.macc,
                    permissionIds: values.permissionIds || []
                };

                if (values.password === 'DO_NOT_CHANGE_PASSWORD') {
                    (updateData as any).password = ""; // Send empty string
                } else if (values.password) {
                    updateData.password = values.password;
                } else {
                    (updateData as any).password = ""; // Send empty string
                }

                await userService.updateUser(editingUser.id, updateData);
                message.success('User updated successfully');
            } else {
                const createData: CreateUserRequest = { ...values };
                await userService.createUser(createData);
                message.success('User created successfully');
            }

            handleCancel();
            fetchData(); // Refresh data;
        } catch (error: any) {
            const errorMessage = error.response?.data || 'Operation failed';
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            setLoading(true);
            await userService.deleteUser(id);
            message.success('User deleted successfully');
            fetchData(); // Refresh data
        } catch (error) {
            message.error('Failed to delete user');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        { title: 'Username', dataIndex: 'username', key: 'username' },
        { title: 'Employee Name', dataIndex: 'employeeName', key: 'employeeName', render: (name: string) => name || 'N/A' },
        { title: 'Mã Chấm Công', dataIndex: 'macc', key: 'macc', render: (macc: string) => macc || 'N/A' },
        { title: 'Khoa Phòng', dataIndex: 'departmentName', key: 'departmentName', render: (name: string) => name || 'N/A' },
        { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt', render: (date: string) => new Date(date).toLocaleDateString() },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: User) => (
                <Space size="middle">
                    <Button type="link" onClick={() => showModal(record)}>Edit</Button>
                    <Popconfirm title="Are you sure to delete this user?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
                        <Button type="link" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const filteredUsers = users.filter(user =>
        user.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Card>
            <Title level={4}>Quản lý tài khoản</Title>
            <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                <Button type="primary" onClick={() => showModal(null)}>
                    Add User
                </Button>
                <Input.Search
                    placeholder="Search by employee name or username"
                    allowClear
                    enterButton="Search"
                    onSearch={(value) => setSearchTerm(value)}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: 300 }}
                />
            </Space>
            <Table columns={columns} dataSource={filteredUsers} rowKey="id" loading={loading} scroll={{ x: 'max-content' }} />

            <Modal
                title={editingUser ? 'Edit User' : 'Add User'}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                confirmLoading={loading}
            >
                <Form form={form} layout="vertical" name="userForm">
                    <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Please input the username!' }]}>
                        <Input disabled={!!editingUser} />
                    </Form.Item>
                    <Form.Item name="password" label="Password" rules={[{ required: !editingUser, message: 'Please input the password!' }]}>
                        <Input.Password />
                    </Form.Item>
                    <Form.Item name="macc" label="Employee">
                        <Select showSearch optionFilterProp="children" placeholder="Select an employee">
                            {editingUser && editingUser.macc && (
                                <Option key={editingUser.macc} value={editingUser.macc}>{editingUser.employeeName}</Option>
                            )}
                            {employees.map(emp => (
                                <Option key={emp.macc} value={emp.macc}>{emp.tennv} ({emp.macc})</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="permissionIds" label="Permissions">
                        <Checkbox.Group options={permissions.map(p => ({ label: p.description, value: p.permissionId }))} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default UserManagement;
